import { ListTable, Loader, Modal, ModalHandle, toArray, Tooltip } from 'Mri.Web.Common';
import { observer } from 'mobx-react';
import React, { useEffect, useMemo, useState } from 'react';
import { AlertModal } from './AlertModals';
import { useStore } from '../../hooks/use-store';
import { Alert } from './AlertStore';
import { Button } from 'reactstrap';
import { isEmpty } from 'lodash';
import classNames from 'classnames';
import { autorun } from 'mobx';
import styles from './Alert.module.scss';
import { BsInfoCircleFill } from '@react-icons/all-files/bs/BsInfoCircleFill';
import { BsCheckCircle } from '@react-icons/all-files/bs/BsCheckCircle';
import { BsXCircle } from '@react-icons/all-files/bs/BsXCircle';

export const AlertList = observer(() => {
    const { contractExpirationAlertStore } = useStore();
    const [alertsLoading, setAlertsLoading] = useState(false);
    const [selectedAlert, setSelectedAlert] = useState({} as Alert);
    const modalHandle = useMemo(() => new ModalHandle(), []);
    const confirmDeleteModalHandle = useMemo(() => new ModalHandle(), []);

    useEffect(() => {
        // eslint-disable-next-line no-console
        setAlertsLoading(true);
        contractExpirationAlertStore.loadAlertsAsync();
        setAlertsLoading(false);
    }, [contractExpirationAlertStore]);

    const createAlert = () => {
        setSelectedAlert({} as Alert);
        autorun(() => modalHandle.open());
    };

    const editAlert = (alert: Alert) => {
        setSelectedAlert(alert);
        autorun(() => modalHandle.open());
    };

    const deleteAlert = () => {
        if (isEmpty(selectedAlert) || !selectedAlert.contractAlertKey) return;
        setAlertsLoading(true);
        contractExpirationAlertStore
            .deleteAlertAsync(selectedAlert.contractAlertKey)
            .then(() => {
                confirmDeleteModalHandle.close();
                setSelectedAlert({} as Alert);
            })
            .finally(() => setAlertsLoading(false));
    };

    const confirmDeleteAlert = (alert: Alert) => {
        setSelectedAlert(alert);
        confirmDeleteModalHandle.open();
    };

    const headCells = [
        { id: 'daysFromExpiration', numeric: true, label: 'Days From Expiration' },
        { id: 'hourOfDay', numeric: true, label: 'Hour of Day' },
        { id: 'minuteOfDay', numeric: true, label: 'Minute of Day' },
        {
            id: 'internalOnly',
            numeric: true,
            label: 'Internal Only',
            customCell: (alert: Alert, key: number) => {
                return (
                    <span>{alert.internalOnly ? <BsCheckCircle color={'green'} /> : <BsXCircle color={'red'} />}</span>
                );
            },
        },
        {
            id: 'edit',
            numeric: false,
            label: 'Edit',
            customCell: (alert: Alert, key: number) => (
                <Button key={key} data-key={key} onClick={() => editAlert(alert)} color={'primary'}>
                    Edit
                </Button>
            ),
        },
        {
            id: 'delete',
            numeric: false,
            label: 'Delete',
            customCell: (alert: Alert, key: number) => (
                <Button key={key} data-key={key} onClick={() => confirmDeleteAlert(alert)} color={'danger'}>
                    Delete
                </Button>
            ),
        },
    ];

    const InfoIcon = () => (
        <>
            <Tooltip
                message={
                    'An expiration alert will be sent to the user that is associated with each account under each contract.'
                }
            >
                <BsInfoCircleFill id="info-icon" className={styles.infoIcon} />
            </Tooltip>
        </>
    );

    const ConfirmDeleteModalFooter = () => (
        <>
            <div className="confirm-footer">
                <Button color={'secondary'} onClick={confirmDeleteModalHandle.close}>
                    Cancel
                </Button>
                <Button color={'danger'} onClick={deleteAlert}>
                    Delete
                </Button>
            </div>
        </>
    );
    const ConfirmDeleteModal = ({ alert, deleteModalHandle }: { alert: Alert; deleteModalHandle: ModalHandle }) => (
        <Modal
            modalHandle={deleteModalHandle}
            className={classNames('p-0')}
            size={'sm'}
            footer={ConfirmDeleteModalFooter()}
        >
            <div className="modal-header">
                <h5 className="modal-title">Confirm Delete</h5>
            </div>
            <div className="modal-body">
                <div>Are you sure you want to delete the {alert.daysFromExpiration} day alert?</div>
            </div>
        </Modal>
    );

    return (
        <>
            <div className="container">
                <div className="card">
                    <div className="card-body">
                        <div className={styles.listHeader}>
                            <h2>
                                Contract Expiration Alerts <InfoIcon />
                            </h2>
                            <button className='btn btn-primary' onClick={createAlert}>
                                Create Alert
                            </button>
                        </div>
                        <div className="border" />
                        <AlertModal
                            alert={selectedAlert}
                            modalHandle={modalHandle}
                            onClosed={() => setSelectedAlert({})}
                        />
                        <ConfirmDeleteModal alert={selectedAlert} deleteModalHandle={confirmDeleteModalHandle} />
                        {!alertsLoading && contractExpirationAlertStore.alerts.size > 0 && (
                            <ListTable
                                rows={toArray(contractExpirationAlertStore.alerts)}
                                headCells={headCells}
                                addSpace={false}
                            />
                        )}
                        {!alertsLoading && contractExpirationAlertStore.alerts.size === 0 && (
                            <div className={'col-12 text-center p-4 text-gray-light'}>
                                <h2>Zero active Contract Expiration Alerts</h2>
                                <button className="btn btn-primary" onClick={createAlert}>
                                    Create Alert
                                </button>
                            </div>
                        )}
                        {alertsLoading && (
                            <div className={'col-12 text-center p-4 text-gray-light'}>
                                <Loader loading={true} message={'Loading Contract Expiration Alerts'} showSpinner />
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </>
    );
});
